/**
 * Bulk Actions Service
 * Handles bulk operations on properties for realtor users
 */

interface BulkActionResult {
  success: boolean;
  message?: string;
  processedIds?: string[];
  failedIds?: string[];
  errors?: Record<string, string>;
}

interface BulkActualizeRequest {
  propertyIds: string[];
  updatePublicationDate?: boolean;
  comment?: string;
}

interface BulkArchiveRequest {
  propertyIds: string[];
  reason?: string;
  comment?: string;
}

class BulkActionsService {
  private baseUrl = process.env.NEXT_PUBLIC_BASE_URL;

  /**
   * Actualize multiple properties
   * Updates publication date and refreshes property status
   */
  async actualize(request: BulkActualizeRequest): Promise<BulkActionResult> {
    try {
      const response = await fetch(
        `${this.baseUrl}/properties/bulk/actualize`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            ...request,
            updatePublicationDate: request.updatePublicationDate ?? true,
          }),
        },
      );

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        return {
          success: false,
          message:
            error.message ||
            `Failed to actualize properties (${response.status})`,
        };
      }

      const result = await response.json();
      return {
        success: true,
        message:
          result.message ||
          `Successfully actualized ${request.propertyIds.length} properties`,
        processedIds: result.processedIds || request.propertyIds,
        failedIds: result.failedIds || [],
      };
    } catch (error) {
      console.error("Bulk actualize error:", error);
      return {
        success: false,
        message: "Network error occurred while actualizing properties",
      };
    }
  }

  /**
   * Archive multiple properties
   * Moves properties to archived status
   */
  async archive(request: BulkArchiveRequest): Promise<BulkActionResult> {
    try {
      const response = await fetch(`${this.baseUrl}/properties/bulk/archive`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        return {
          success: false,
          message:
            error.message ||
            `Failed to archive properties (${response.status})`,
        };
      }

      const result = await response.json();
      return {
        success: true,
        message:
          result.message ||
          `Successfully archived ${request.propertyIds.length} properties`,
        processedIds: result.processedIds || request.propertyIds,
        failedIds: result.failedIds || [],
      };
    } catch (error) {
      console.error("Bulk archive error:", error);
      return {
        success: false,
        message: "Network error occurred while archiving properties",
      };
    }
  }

  /**
   * Restore multiple properties from archive
   */
  async restore(propertyIds: string[]): Promise<BulkActionResult> {
    try {
      const response = await fetch(`${this.baseUrl}/properties/bulk/restore`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ propertyIds }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        return {
          success: false,
          message:
            error.message ||
            `Failed to restore properties (${response.status})`,
        };
      }

      const result = await response.json();
      return {
        success: true,
        message:
          result.message ||
          `Successfully restored ${propertyIds.length} properties`,
        processedIds: result.processedIds || propertyIds,
        failedIds: result.failedIds || [],
      };
    } catch (error) {
      console.error("Bulk restore error:", error);
      return {
        success: false,
        message: "Network error occurred while restoring properties",
      };
    }
  }

  /**
   * Update status for multiple properties
   */
  async updateStatus(
    propertyIds: string[],
    status: string,
  ): Promise<BulkActionResult> {
    try {
      const response = await fetch(
        `${this.baseUrl}/properties/bulk/update-status`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ propertyIds, status }),
        },
      );

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        return {
          success: false,
          message:
            error.message ||
            `Failed to update property status (${response.status})`,
        };
      }

      const result = await response.json();
      return {
        success: true,
        message:
          result.message ||
          `Successfully updated status for ${propertyIds.length} properties`,
        processedIds: result.processedIds || propertyIds,
        failedIds: result.failedIds || [],
      };
    } catch (error) {
      console.error("Bulk update status error:", error);
      return {
        success: false,
        message: "Network error occurred while updating property status",
      };
    }
  }

  /**
   * Assign multiple properties to a realtor
   */
  async assign(
    propertyIds: string[],
    assigneeId: string,
  ): Promise<BulkActionResult> {
    try {
      const response = await fetch(`${this.baseUrl}/properties/bulk/assign`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ propertyIds, assigneeId }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        return {
          success: false,
          message:
            error.message || `Failed to assign properties (${response.status})`,
        };
      }

      const result = await response.json();
      return {
        success: true,
        message:
          result.message ||
          `Successfully assigned ${propertyIds.length} properties`,
        processedIds: result.processedIds || propertyIds,
        failedIds: result.failedIds || [],
      };
    } catch (error) {
      console.error("Bulk assign error:", error);
      return {
        success: false,
        message: "Network error occurred while assigning properties",
      };
    }
  }

  /**
   * Export properties data
   */
  async export(
    propertyIds: string[],
    format: "csv" | "xlsx" = "csv",
  ): Promise<BulkActionResult> {
    try {
      const response = await fetch(`${this.baseUrl}/properties/bulk/export`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ propertyIds, format }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        return {
          success: false,
          message:
            error.message || `Failed to export properties (${response.status})`,
        };
      }

      // Handle file download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `properties-export-${Date.now()}.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      return {
        success: true,
        message: `Successfully exported ${propertyIds.length} properties`,
        processedIds: propertyIds,
      };
    } catch (error) {
      console.error("Bulk export error:", error);
      return {
        success: false,
        message: "Network error occurred while exporting properties",
      };
    }
  }
}

export const bulkActionsService = new BulkActionsService();
export type { BulkActionResult, BulkActualizeRequest, BulkArchiveRequest };
